/**
 * Création / cleanup de l'iframe widget en mode `inline`.
 *
 * Le SDK reproduit ici la logique du script inline embarqué dans
 * `buildEmbedIframeHtml` (snippet legacy) — mais en gardant la référence au
 * listener pour pouvoir le retirer proprement au `destroy()`.
 */

import { mergeCheckoutCompleteCallback } from './callbackHelpers';
import { buildWidgetEmbedUrl, normalizeBaseUrl, DEFAULT_WIDGET_BASE_URL, withSdkHostContext } from './url';
import { attachResizeListener, resolveBaseOrigin } from './resize';
import {
  createEventDispatcher,
  resolveAllowedOrigins,
} from './dispatcher';
import type { RintintinBookingInitOptions, RintintinBookingInstance } from './types';

/** Hauteur initiale de l'iframe (px). Le ResizeObserver de l'embed prend le relais. */
export const INLINE_INITIAL_HEIGHT_PX = 500;

const LOG_PREFIX = '[RintintinBooking]';

/**
 * Résout le container : sélecteur CSS → premier match, ou élément DOM tel quel.
 * Retourne `null` si introuvable (le caller doit logger l'erreur et abort).
 */
function resolveContainer(container: string | HTMLElement | undefined): HTMLElement | null {
  if (!container) return null;
  if (typeof container === 'string') {
    try {
      return document.querySelector<HTMLElement>(container);
    } catch {
      // sélecteur invalide → on log côté caller, ici on renvoie juste null
      return null;
    }
  }
  // HTMLElement passé directement : on l'utilise tel quel
  return container;
}

/**
 * Crée et insère l'iframe inline dans le container, attache le listener resize
 * filtré sur l'iframe + l'origine baseUrl, et retourne une instance avec
 * `destroy()` pour démonter proprement.
 */
export function mountInlineWidget(
  options: RintintinBookingInitOptions
): RintintinBookingInstance | null {
  const container = resolveContainer(options.container);
  if (!container) {
    // Sélecteur introuvable ou container manquant : on log et on abort proprement
    // sans throw pour ne pas casser la page hôte.
    console.error(
      `${LOG_PREFIX} container introuvable :`,
      options.container,
      '— widget non monté.'
    );
    return null;
  }

  const embedUrl = buildWidgetEmbedUrl(withSdkHostContext(options));
  const normalizedBase = normalizeBaseUrl(options.baseUrl || DEFAULT_WIDGET_BASE_URL);
  const baseOrigin = resolveBaseOrigin(normalizedBase);
  const allowedOrigins = resolveAllowedOrigins(options.allowedOrigins, normalizedBase);

  // Création de l'iframe : on aligne les attributs sur le snippet legacy
  // (loading, referrerpolicy, title, style) pour ne pas régresser sur la
  // sécurité / a11y / performance.
  const iframe = document.createElement('iframe');
  iframe.src = embedUrl;
  iframe.title = 'Réserver — Rintintin';
  iframe.setAttribute('width', '100%');
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('loading', 'lazy');
  iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
  iframe.style.border = '0';
  iframe.style.width = '100%';
  iframe.style.height = `${INLINE_INITIAL_HEIGHT_PX}px`;
  iframe.style.display = 'block';

  container.appendChild(iframe);

  // Listener resize partagé avec popup.ts — filtre strictement sur source + origine.
  // Conservé pour la rétrocompat avec le format legacy `rtt-widget-resize`.
  const detachResize = attachResizeListener(iframe, baseOrigin);

  // Dispatcher Phase 4 : route les events versionnés vers les callbacks utilisateur.
  // Indépendant du listener resize ci-dessus (deux listeners message coexistent).
  const dispatcher = createEventDispatcher({
    iframe,
    allowedOrigins,
    callbacks: mergeCheckoutCompleteCallback(options, () => {
      iframe.src = embedUrl;
    }),
  });

  let destroyed = false;
  return {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      detachResize();
      dispatcher.destroy();
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    },
  };
}
